<template>
  <div class="my-apply">
    <h3>我的申请</h3>
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/yj/work' }">合同管理</el-breadcrumb-item>
      <el-breadcrumb-item>我的申请</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="apply-container">
      <!-- 搜索和筛选 -->
      <el-form :inline="true" class="search-form" size="small">
        <el-form-item label="项目名称">
          <el-input
            v-model="searchForm.workName"
            placeholder="搜索项目名称"
            clearable
            @clear="handleSearch"
          />
        </el-form-item>
        <el-form-item label="申请状态">
          <el-select v-model="searchForm.status" placeholder="全部" clearable @clear="handleSearch">
            <el-option label="待审核" :value="0" />
            <el-option label="已通过" :value="1" />
            <el-option label="已拒绝" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch" :loading="loading">
            搜索
          </el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 申请列表 -->
      <el-table
        :data="applyList"
        border
        v-loading="loading"
        class="apply-table"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="id" label="申请ID" width="80" sortable="custom" />
        <el-table-column prop="WorkName" label="项目名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="build_company" label="建设单位" min-width="120" show-overflow-tooltip />
        <el-table-column label="修改内容" min-width="100">
          <template slot-scope="scope">
            <el-tag size="small" type="info">
              修改{{ scope.row.changedCount || 0 }}项
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="apply_time" label="申请时间" width="150" sortable="custom">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.apply_time) }}
          </template>
        </el-table-column>
        <el-table-column label="申请状态" width="100">
          <template slot-scope="scope">
            <el-tag 
              size="small" 
              :type="getStatusTagType(scope.row.status)"
            >
              {{ scope.row.statusText || getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="review_user_id" label="审批人" width="100">
          <template slot-scope="scope">
            {{ scope.row.review_user_id ? (scope.row.reviewUserName || '用户' + scope.row.review_user_id) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="review_time" label="审批时间" width="150">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.review_time) }}
          </template>
        </el-table-column>
        <el-table-column prop="review_remark" label="审批备注" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.review_remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              size="mini"
              @click="handleViewDetail(scope.row)"
            >
              查看详情
            </el-button>
            <el-button
              v-if="scope.row.status === 0"
              type="text"
              size="mini"
              style="color: #E6A23C"
              @click="handleWithdraw(scope.row)"
            >
              撤回
            </el-button>
            <el-button
              v-if="scope.row.status === 2"
              type="text"
              size="mini"
              style="color: #409EFF"
              @click="handleReApply(scope.row)"
            >
              重新申请
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.current"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pagination.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        class="pagination"
      />
    </div>

    <!-- 申请详情对话框 -->
    <el-dialog
      title="申请详情"
      :visible.sync="detailDialogVisible"
      width="80%"
      top="5vh"
      @closed="handleDialogClosed"
    >
      <apply-detail
        v-if="detailDialogVisible && detailData.applyId"
        :key="detailData.applyId"
        :apply-id="detailData.applyId"
        :can-approve="false"
        @approved="handleRefresh"
        @rejected="handleRefresh"
      />
    </el-dialog>
  </div>
</template>

<script>
import { getContractApplyList, withdrawContractApply } from '../api';
import { mapGetters } from 'vuex';
import ApplyDetail from '../components/ApplyDetail.vue';

export default {
  name: 'MyApply',
  
  components: {
    ApplyDetail
  },
  
  data() {
    return {
      loading: false,
      applyList: [],
      
      // 搜索表单
      searchForm: {
        workName: '',
        status: null
      },
      
      // 分页
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      
      // 排序
      sortField: 'apply_time',
      sortOrder: 'desc',
      
      // 详情对话框
      detailDialogVisible: false,
      detailData: {
        applyId: null
      }
    };
  },
  
  computed: {
    ...mapGetters(['userId', 'roleId'])
  },
  
  created() {
    this.loadData();
  },
  
  methods: {
    // 加载数据
    async loadData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pagination.size,
          userId: this.userId,
          roleId: this.roleId // 传递角色ID，后端会自动筛选该用户的申请
        };
        
        // 添加搜索条件
        if (this.searchForm.workName) {
          params.workName = this.searchForm.workName;
        }
        if (this.searchForm.status !== null && this.searchForm.status !== '') {
          params.status = this.searchForm.status;
        }
        
        // 添加排序
        if (this.sortField) {
          params.field = this.sortField;
          params.order = this.sortOrder;
        }
        
        const res = await getContractApplyList(params);
        
        if (res.code === 0) {
          this.applyList = res.data || [];
          this.pagination.total = res.count || 0;
        } else {
          this.$message.error(res.msg || '加载失败');
        }
      } catch (error) {
        console.error('加载申请列表失败:', error);
        this.$message.error('加载失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.current = 1;
      this.loadData();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchForm = {
        workName: '',
        status: null
      };
      this.pagination.current = 1;
      this.loadData();
    },
    
    // 排序变化
    handleSortChange({ prop, order }) {
      if (prop && order) {
        this.sortField = prop;
        this.sortOrder = order === 'ascending' ? 'asc' : 'desc';
      } else {
        this.sortField = 'apply_time';
        this.sortOrder = 'desc';
      }
      this.loadData();
    },
    
    // 分页大小变化
    handleSizeChange(size) {
      this.pagination.size = size;
      this.pagination.current = 1;
      this.loadData();
    },
    
    // 当前页变化
    handleCurrentChange(current) {
      this.pagination.current = current;
      this.loadData();
    },
    
    // 查看详情
    handleViewDetail(row) {
      this.detailData = {
        applyId: row.id
      };
      this.detailDialogVisible = true;
    },
    
    // 撤回申请
    async handleWithdraw(row) {
      try {
        await this.$confirm('确定要撤回此申请吗？撤回后可以重新申请。', '提示', {
          confirmButtonText: '确定撤回',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        const res = await withdrawContractApply({
          applyId: row.id
        });
        
        if (res.code === 0) {
          this.$message.success('申请已撤回');
          this.loadData();
        } else {
          this.$message.error(res.msg || '撤回失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('撤回申请失败:', error);
          this.$message.error('撤回失败');
        }
      }
    },

    // 重新申请（已拒绝的申请）
    handleReApply(row) {
      const contractId = row.contract_id;
      if (!contractId) {
        this.$message.warning('无法获取合同信息，请稍后重试');
        return;
      }
      this.$router.push({
        path: '/yj/contract/apply',
        query: { contractId }
      });
    },
    
    // 处理刷新
    handleRefresh() {
      this.detailDialogVisible = false;
      this.loadData();
    },
    
    // 对话框关闭
    handleDialogClosed() {
      this.detailData = {
        applyId: null
      };
    },
    
    // 获取状态文本
    getStatusText(status) {
      switch (status) {
        case 0: return '待审核';
        case 1: return '已通过';
        case 2: return '已拒绝';
        default: return '未知';
      }
    },
    
    // 获取状态标签类型
    getStatusTagType(status) {
      switch (status) {
        case 0: return 'warning';
        case 1: return 'success';
        case 2: return 'danger';
        default: return 'info';
      }
    },
    
    // 格式化日期时间
    formatDateTime(datetime) {
      if (!datetime) return '-';
      const date = new Date(datetime);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped>
.my-apply {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.breadcrumb {
  margin-bottom: 20px;
}

.apply-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.search-form {
  margin-bottom: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}

.apply-table {
  margin-bottom: 15px;
  font-size: 13px;
}

.pagination {
  text-align: right;
  padding: 10px 0;
}

/* 响应式适配 */
@media screen and (max-width: 768px) {
  .my-apply {
    padding: 10px;
  }
  
  .apply-container {
    padding: 12px;
  }

  .search-form {
    padding: 10px;
    display: flex;
    flex-wrap: wrap;
  }

  .search-form .el-form-item {
    width: 100%;
    margin-bottom: 8px;
  }
  
  .apply-table {
    font-size: 12px;
  }

  .pagination {
    text-align: center;
  }
}
</style>
